import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

interface ResourceItem {
  id: string | number;
  [key: string]: unknown;
}

export interface UseSubscriptionOptions<T = unknown> extends Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> {
  queryKey: unknown[];
  queryFn: () => Promise<T>;
  subscriptionFn?: () => WebSocket | null;
  enabled?: boolean;
}

export function useSubscription<T = unknown>({ queryKey, queryFn, subscriptionFn, enabled = true, ...queryOptions }: UseSubscriptionOptions<T>) {
  const queryClient = useQueryClient();
  const result = useQuery<T>({ queryKey, queryFn, enabled, ...queryOptions });

  const subscriptionFnRef = useRef(subscriptionFn);
  subscriptionFnRef.current = subscriptionFn;

  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  const keyHash = JSON.stringify(queryKey);

  useEffect(() => {
    if (!subscriptionFnRef.current || enabled === false) return;

    let ws: WebSocket | null;
    try {
      ws = subscriptionFnRef.current();
    } catch {
      return;
    }
    if (!ws) return;

    const handleMessage = (msgEvent: MessageEvent) => {
      try {
        const message = JSON.parse(msgEvent.data);
        const { event, data } = message;
        if (!event || data === undefined) return;

        queryClient.setQueryData<T>(queryKeyRef.current, (oldData) => {
          if (oldData == null) return oldData;

          if (Array.isArray(oldData)) {
            switch (event) {
              case 'created':
                return [...oldData, data] as T;
              case 'updated':
                return oldData.map((item: ResourceItem) =>
                  item.id === data.id ? { ...item, ...data } : item
                ) as T;
              case 'deleted':
                return oldData.filter((item: ResourceItem) => item.id !== data.id) as T;
              default:
                return oldData;
            }
          }

          // Single resource (detail view)
          const oldItem = oldData as unknown as ResourceItem;
          if (event === 'updated' && data.id === oldItem.id) {
            return { ...oldData, ...data };
          }
          if (event === 'deleted' && data.id === oldItem.id) {
            return null as T;
          }
          return oldData;
        });
      } catch {
        // ignore malformed messages
      }
    };

    ws.addEventListener('message', handleMessage);

    return () => {
      ws!.removeEventListener('message', handleMessage);
      ws!.close();
    };
  }, [keyHash, enabled, queryClient]);

  return {
    ...result,
    loading: result.isLoading,
  };
}
